import { PageState } from '../../base-page';
import { Utilities } from '../../helper/util';
import { Log } from '../../log';
import { AlertControllerMessage, EntityFactoryInterface } from './entity-factory-interface';

export enum EntityType {
    Auction = 'Auction',
    Category = 'Category',
    Donation = 'Donation',
    Event = 'Event',
    FundraisingCampaign = 'Fundraising Campaign',
    Membership = 'Membership',
    Newsletter = 'Newsletter',
    Page = 'Page',
    Post = 'News item',
    Product = 'Product',
    Raffle = 'Raffle',
    Resource = 'Resource',
    Special = 'Special',
    Sponsor = 'Sponsor',
    Survey = 'Survey',
    TicketType = 'Ticket Type',
}

/**
 * This Entity class acts as a central manager for basic CRUD operations of model objects.
 * It must be subclassed, and the subclass must implement some conventions for it to work
 * properly, at a minimum:
 * 
 *   static className = 'Page';
 *   static idField = 'pageId';
 * 
 * These variables are used to look up the primary key and generate error messages.
 * 
 * Because this is the central control for CRUD, it also supports change checking and 
 * caching. Change checking is handled by keeping a copy of the server object and 
 * passing back a working copy. Then we can compare the two to detect changes.
 * 
 * Caching is only meant to cache the object currently being viewed/manipulated.
 * I.e. when we load an object with getById, we cache it in a static variable, and on subsequent
 * load, we'll see the cached object has same PK, so we return the cached version.
 * So for change checking and caching, we need to add these static properties to the 
 * implementing model class:
 * 
 *  private static _cachedServerObj: Page; // unmodified until saved
 *  private static _cachedObj: Page; // working copy  
 * 
 * The next complexity comes from child objects. In the case of Page objects, each Page
 * has any number of child PageBlock objects. We used to fetch child objects as an array 
 * in the parent object, but we are splitting this up to simplify the code. Now, the 
 * child objects are loaded and managed separately.
 * 
 * But because we need to show all child objects at once, in a list or rendered some way,
 * we can't rely on the same one-object-at-a-time-caching like we do for the parent 
 * object. Instead, these child objects are loaded by parent id vs individually, and 
 * the whole array is cached. For these types of objects, the implementing model class
 * will need these static properties
 * 
 *  private static _cachedForParentId: number; // keep track of which page the blocks are for 
 *  private static _cachedServerObjs: PageBlock[]; // unmodified until saved
 *  private static _cachedObjs: PageBlock[]; // working copy
 * 
 * There is a special function for loading and caching child arrays: fetchChildrenFor
 * This function wlil use the above properties to see if we already have the desired array,
 * fetching and caching if not, otherwise returning.
 * 
 * The tricky part is now on most other functions like hasChanges, revert, save and delete,
 * we need to check to see if we are manipulating a child array object or not. If we are,
 * then we need to actually find the object within the array to check for changes, revert it,
 * update it in server copy or remove it from array (both working and server copies).
 * 
 */
export class Entity {

    static factory: EntityFactoryInterface;

    public didSave: boolean = false;

    get pk() {
        return this[this.constructor['idField']]
    }
    set pk(val: number) {
        this[this.constructor['idField']] = val;
    }

    constructor(public className?: string, public idField: string = 'id', public controller?: string) {

        // ensure we always have a controller so our localstorage entity factory knows where to save/read from
        // if ( controller == null ) {
        //     this.controller = this.constructor['className'];
        // }
    }

    // compare the working object with the unmodified server object to see if any edits have been made
    // if working with single cached object, easy peasy. If working with child array, need to pass in pk
    // and find in array.
    static hasChanges<Obj>(objType: new (init?: any) => Obj, childId?: number) {
        if (childId > 0 && objType['_cachedForParentId'] > 0) {
            const idx = Entity.indexOfCachedChild(objType, childId);

            // if we have a raw type, we'll use that to complete the comparison, otherwise just compare the full
            // objects that we have cached
            if (objType['_rawType'] != null) {
                const srvCopy = new objType['_rawType'](objType['_cachedServerObjs'][idx]);
                const wipCopy = new objType['_rawType'](objType['_cachedObjs'][idx]);
                return JSON.stringify(srvCopy) !== JSON.stringify(wipCopy);
            } else {
                // Log.debug('working copy: \n' + JSON.stringify(objType['_cachedObjs'][idx]))
                // Log.debug('server copy: \n'+ JSON.stringify(objType['_cachedServerObjs'][idx]));
                return (JSON.stringify(objType['_cachedObjs'][idx]) !== JSON.stringify(objType['_cachedServerObjs'][idx]));
            }

        } else {
            // this would let us specify a raw type that doesn't have any object initialization woes
            // if ( objType['_rawType'] != null ) {
            //     const srvCopy = new objType['_rawType']( objType['_cachedServerObj'] );
            //     const wipCopy = new objType['_rawType']( objType['_cachedObj'] );
            //     const isDiff =  JSON.stringify(srvCopy) !== JSON.stringify(wipCopy);
            //     if ( isDiff == true) {
            //         Log.debug(JSON.stringify(srvCopy))
            //         Log.debug(JSON.stringify(wipCopy));
            //         Entity.findDifferences( srvCopy, wipCopy );
            //     }
            //     return isDiff;
            // } else {

            const server = JSON.stringify(objType['_cachedServerObj']);
            const cached = JSON.stringify(objType['_cachedObj']);
            let isDiff = server !== cached;
            if (isDiff) {
                const serverSorted = Entity.sortedStringify(objType['_cachedServerObj']);
                const cachedSorted = Entity.sortedStringify(objType['_cachedObj']);
                isDiff = serverSorted !== cachedSorted;
            }

            if (isDiff) {
                Entity.findDifferences(objType['_cachedObj'], objType['_cachedServerObj']);
            }
            return isDiff;
        }
    }

    static findDifferences(obj1: any, obj2: any) { //}: string[] {
        if (obj1 == null || obj2 == null) {
            Log.debug('one was null')
            return;
        }
        const keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
        // const differentKeys: string[] = [];

        Log.debug('Comparing objects for differences: ')
        keys.forEach((key) => {
            // if (obj1[key] != obj2[key]) {
            //     // differentKeys.push(key);
            //     Log.debug('key not matching: ' + key)
            // } else 
            if (JSON.stringify(obj1[key]) !== JSON.stringify(obj2[key])) {
                Log.debug('value for key ' + key + ' not matching: ')

                Log.debug('  cached local:  ' + JSON.stringify(obj1[key]))
                Log.debug('  cached server: ' + JSON.stringify(obj2[key]))
                // differentKeys.push(key);
            }
        });

        // return differentKeys;
    }

    // discard changes in the working object by re-instantiating from the unmodified server object
    static revert<Obj>(objType: new (init?: any) => Obj, chilkdPk?: number) {
        if (chilkdPk > 0 && objType['_cachedForParentId'] > 0) {
            const idx = Entity.indexOfCachedChild(objType, chilkdPk);
            // think this breaks the binding, need to do object assign instead
            //objType['_cachedObjs'][idx] = new objType(objType['_cachedServerObjs'][idx]);
            Entity.forceAssignNull(objType['_cachedObjs'][idx], objType['_cachedServerObjs'][idx]);
        } else if (objType['_cachedObj'].hasOwnProperty('pageId') && objType['_cachedObj']['pageId'] > 0 == false) {
            // if this is a page and hasn't been saved, just clear it out of cache
            objType['_cachedObj'] = undefined;
            objType['_cachedServerObj'] = undefined
        } else {
            // objType['_cachedObj'] = new objType(objType['_cachedServerObj']);
            Entity.forceAssignNull(objType['_cachedObj'], objType['_cachedServerObj']);
        }
    }

    static updateCached<Obj>(objType: new (init?: any) => Obj, obj: Obj, chilkdPk?: number) {
        // fetch object and store in cache
        objType['_cachedObj'] = obj

        // start a timer for when this object will be considered expired in cache
        objType['_cacheExpiry'] = new Date().getTime() + (5 * 60 * 1000); // 5 minute timeout

        // make an immutable server copy for change checking
        objType['_cachedServerObj'] = new objType(objType['_cachedObj']);

        return objType['_cachedObj'];
    }


    static forceAssignNull(target: any, source: any) {
        // Remove keys that are in target but not in source
        Object.keys(target).forEach(key => {
            if (!(key in source)) {
                delete target[key];
            }
        });

        // Assign values from source to target
        Object.keys(source).forEach(key => {
            target[key] = source[key];
        });
    }

    // get by id from server and cache it, unless our cached object is already for the same id, then just return it
    // We may want to make caching optional and specified as paramater?
    static async getById<Obj>(objType: new (init?: any) => Obj, id?: number, controller: string = '', endpoint: string = '', silent: boolean = false) {

        // check if we have the requested object in the cache and expiry hasn't passed, if yes, return it
        // this could in theory even be a new empty object, unless we explicitly clear it.
        if (objType.hasOwnProperty('_cachedObj') && // don't cache if we haven't specified caching static on class
            objType['_cachedObj'] != null &&
            objType['_cachedObj'][objType['idField']] == id &&
            objType['_cacheExpiry'] > new Date().getTime()) {
            objType['_cacheExpiry'] = new Date().getTime() + (5 * 60 * 1000); // update expiry since we fetch
            return objType['_cachedObj'];
        }

        // if we are requesting by id 0, it means we are creating a new object, so lets just
        // instantiate a new object of given type and return it to start working on.
        if (id > 0 == false) {
            objType['_cachedObj'] = new objType();
            objType['_cacheExpiry'] = new Date().getTime() + (5 * 60 * 1000); // update expiry since we fetch
            return objType['_cachedObj'];
        }

        // build up endpoint to use. Should almost always just be the id, but could be some custom endpoint plus id
        const ep = (String.isNullOrWhiteSpace(endpoint)) ? `${id}` : `${endpoint}/${id}`;

        // fetch object and store in cache
        objType['_cachedObj'] = new objType(await Entity.factory.getObj(objType, controller, ep, 'json', silent) as Obj);

        // start a timer for when this object will be considered expired in cache
        objType['_cacheExpiry'] = new Date().getTime() + (5 * 60 * 1000); // 5 minute timeout

        // make an immutable server copy for change checking
        objType['_cachedServerObj'] = new objType(objType['_cachedObj']);

        return objType['_cachedObj'];
    }

    // special fetch for when we want to get all the children for a specific entity and 
    // be able to manipulate those independantly of the parent.
    //
    // This should only be called from the parent object so it can pass in the correct
    // endpoint (mostly for localhost entity factory)
    protected static async fetchChildrenFor<Obj>(objType: new (init?: any) => Obj, id: number, controller, endpoint, silent: boolean = false) {
        try {
            // check cache and return if we already have
            if (objType['cacheByParent'] == true &&
                objType['_cachedForParentId'] == id &&
                objType['_cachedObjs'] != null &&
                objType['_cacheExpiry'] > new Date().getTime()) {
                objType['_cacheExpiry'] = new Date().getTime() + (5 * 60 * 1000); // update expiry
                return objType['_cachedObjs'];
            }

            // fetch children from server
            const resp = await Entity.factory.fetchObj(objType, controller, endpoint, 'json', silent) as Obj[];

            if (objType['cacheByParent'] == true) {
                // setup all caching
                objType['_cachedForParentId'] = id;
                objType['_cachedObjs'] = []
                objType['_cachedServerObjs'] = []
                for (let i = 0; i < resp.length; i++) {
                    objType['_cachedObjs'].push(new objType(resp[i]));
                    objType['_cachedServerObjs'].push(new objType(resp[i]));
                }

                // start a timer for when this cache will be considered expired
                objType['_cacheExpiry'] = new Date().getTime() + (5 * 60 * 1000); // 5 minute timeout

                return objType['_cachedObjs'];
            } else {
                return resp;
            }

        } catch (error) {
            console.error(error);
            return [];
        }
    }

    // in case we ever want to just go direct to server and skip cache
    static async getByIdNoCache<Obj>(objType: new (init?: any) => Obj, id?: number, controller: string = '', endpoint: string = '', silent: boolean = false) {
        const ep = (String.isNullOrWhiteSpace(endpoint)) ? `${id}` : `${endpoint}/${id}`;
        return await Entity.factory.getObj(objType, controller, ep, 'json', silent) as Obj;
    }

    // instead of getById, this could be used for something like getLatest or getLargest, etc.
    // not bothering with cache on this one
    static async getObject<Obj>(objType: new (init?: any) => Obj, controller: string = '', endpoint: string = '', runSilent: boolean = false) {
        return await Entity.factory.getObj(objType, controller, endpoint, 'json', runSilent) as Obj;
    }

    // fetches all from server and caches to avoid continually fetching same list over and over again.
    // Should be used for infrequntly changing but often referenced items like Categories.
    // Technically this could be used instead of fetchChildrenFor, but in that case, we want to explicitly
    // be maintaining the cache, updating records therein. 
    // This version is more brutal in that it is simply cleared in case of any edit.
    static async fetchArrayAndCache<Obj>(objType: new (init?: any) => Obj, controller: string = '', endpoint: string = '', runSilent: boolean = false) {
        try {
            // check cache and return if we already have
            if (objType['_cachedArray'] != null) {
                return objType['_cachedArray'];
            }

            // fetch children from server
            const resp = await Entity.fetchArray(objType, controller, endpoint, 'json', runSilent) as Obj[];

            // add to cache
            objType['_cachedArray'] = [];
            for (let i = 0; i < resp.length; i++) {
                objType['_cachedArray'].push(new objType(resp[i]));
            }
            return objType['_cachedArray'] as Obj[];

        } catch (error) {
            console.error(error);
            return [];
        }
    }

    // fetch array of objects from some endpoint less specific that fetchChildrenFor
    // not bothering with cache on this one. There is another function t
    static async fetchArray<Obj>(objType: new (init?: any) => Obj, controller: string = '', endpoint: string = '', responseType: string = 'json', hideLoad: boolean = false) {
        try {
            return await Entity.factory.fetchObj(objType, controller, endpoint, responseType, hideLoad) as Obj[];
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    // fetch untyped response
    static async fetchUntyped(desc: string, controller: string = '', endpoint: string = '', respType: string = 'json', hideLoad: boolean = false) {
        try {
            return await Entity.factory.getRaw(desc, controller, endpoint, respType, hideLoad);
        } catch (error) {
            throw error;
        }
    }

    // fetch arrayof objects matching passed in search term, also not cached
    static async findMatching<Obj>(objType: new (init?: any) => Obj, term: string, controller: string = '', endpoint: string = 'filter',) {
        try {
            return await Entity.factory.fetchObj(objType, controller, `${endpoint}/${term}`, 'json', true) as Obj[];
        } catch (error) {
            return null;
        }
    }

    // save an object, passes in pageState so we can throw up 'Saving...' indicator
    async save(busyState: PageState = null, controller: string | undefined = this.controller, endpoint: string = '', hideLoad: boolean = false): Promise<boolean> {
        try {
            if (busyState != null) { busyState.busy = true };
            this.didSave = true; // flag that we just saved
            const isUpdate = this.isUpdate(this);
            Object.assign(this, await Entity.factory.saveObj(this, controller, endpoint, `json`, hideLoad) as Entity);

            // now we need to update our cached server copy so 'hasChanges' doesn't incorrectly report that it does
            // first we need to detect if we have a single cached object or a cached array object
            if (this.constructor['_cachedForParentId'] > 0) {
                if (isUpdate) {
                    // update object in array
                    const idx = Entity.indexOfCachedChild(this.constructor as any, this[this.constructor['idField']]);
                    // need to use Object.assign so we don't break binding
                    Object.assign(this.constructor['_cachedObjs'][idx], this);
                    Object.assign(this.constructor['_cachedServerObjs'][idx], this);
                } else {
                    // new child object, push on end
                    this.constructor['_cachedObjs'].push(this);
                    this.constructor['_cachedServerObjs'].push(JSON.parse(JSON.stringify(this)));
                }
            } else if (this.constructor.hasOwnProperty('_cachedObj')) {

                // can we just replace both server and work in progress copy with newly constructed objects?
                this.constructor['_cachedObj'] = this.clone();

                this.constructor['_cachedServerObj'] = this.clone();

                // // on any save we can just update working copy and server copy
                // if ( this.constructor['_cachedObj'] != null ) {
                //     Object.assign(this.constructor['_cachedObj'], this);


                //     // Previously we were just object.assigning or JSON.parsing, but that 
                //     // run the constructor initialization, we need to actually create a new object,
                //     // in which case it doesn't matter if we already had one or not
                //     this.constructor['_cachedServerObj'] = 
                //         new (Object.getPrototypeOf(this).constructor)(JSON.parse(JSON.stringify(this)));

                //     // // only update server copy if we have one, i.e. we don't when we first
                //     // // create an object
                //     // if ( this.constructor['_cachedServerObj'] != null ) {
                //     //     Object.assign(this.constructor['_cachedServerObj'], JSON.parse(JSON.stringify(this)));
                //     // } else {
                //     //     // else just cache this as the server object, but we need to make a 
                //     //     // copy or we're just sharing a reference
                //     //     this.constructor['_cachedServerObj'] = JSON.parse(JSON.stringify(this))
                //     // }
                // } else {
                //     this.constructor['_cachedObj'] = this;
                //     this.constructor['_cachedServerObj'] = JSON.parse(JSON.stringify(this));
                // }
            }
            // clear All cache if we have one
            // THIS IS PROBABLY ALL WE NEED ONCE WE HAVE API, BUT FOR NOW WE CAN'T BLOW AWAY PRE-LOADED
            //this.constructor['_cachedArray'] = null; 

            // for now lets update in cache
            if (this.constructor['_cachedArray'] != null) {
                // update object in array
                var obj = this.constructor['_cachedArray'].find(m => m[(this.constructor['idField'])] == this[this.constructor['idField']]);
                const idx = this.constructor['_cachedArray'].indexOf(obj);
                if (idx >= 0) {
                    Object.assign(this.constructor['_cachedArray'][idx], this);
                } else {
                    this.constructor['_cachedArray'].push(this);
                }
            }

            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            if (busyState != null) { busyState.busy = false };
        }
    }

    clone() {
        return new (Object.getPrototypeOf(this).constructor)(JSON.parse(JSON.stringify(this)));
    }

    equals(obj: Entity) {
        return Entity.sortedStringify(this) === Entity.sortedStringify(obj);
    }

    static sortedStringify(obj: Entity): string {
        if (obj == null) {
            return '';  
        } else {
            return JSON.stringify(obj, Object.keys(obj).sort());
        }
    }

    async reload(controller: string | undefined = this.controller, endpoint: string = '') {
        endpoint = this.getUpdateUrl(this, endpoint);
        Object.assign(this, await Entity.factory.fetchObj(Entity, controller, endpoint));
    }

    async delete(busyState: PageState, controller?: string, endpoint?: string, confirm: boolean = true, message?: AlertControllerMessage) {
        try {
            if (busyState != null) { busyState.busy = true };
            const rslt = await Entity.factory.deleteObj(this, controller, endpoint, confirm, message);
            if (rslt == true) {
                //  nuke any associated generic cached array if one exist
                this.constructor['_cachedArray'] = null;

                if (this.constructor['_cachedForParentId'] > 0) {
                    // remove from child array caches
                    const idx = Entity.indexOfCachedChild(this.constructor as any, this[this.constructor['idField']]);
                    this.constructor['_cachedObjs'].splice(idx, 1);
                    this.constructor['_cachedServerObjs'].splice(idx, 1);
                } else {
                    // on any save we can just update working copy and server copy
                    this.constructor['_cachedObj'] = null;
                    this.constructor['_cachedServerObj'] = null;
                }
            }
            return rslt;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            if (busyState != null) { busyState.busy = false };
        }
    }

    async uploadBase64Images(base64Imgs: string[], controller: string, endpoint: string,): Promise<any> {
        return await Entity.factory.uploadBase64Images(this, base64Imgs, controller, endpoint)
    }

    isUpdate(entity: Entity) {
        const id = entity[this.constructor['idField'] as keyof Entity];
        // if the id is a number, check if it is greater than 0, if it's a string then
        // check if it's not empty
        return (id != null && (typeof id === 'number' ? id > 0 : (id as string).length > 0));
    };

    getUpdateUrl(entity: Entity, endpoint: string) {
        return `${endpoint}/${entity[this.constructor['idField'] as keyof Entity]}`;
    };

    private static indexOfCachedChild<Obj>(objType: new (init?: any) => Obj, childId?: number) {
        var obj = objType['_cachedObjs'].find(m => m[(objType['idField'])] == childId);
        return objType['_cachedObjs'].indexOf(obj);
    }

}
